import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Upload, X, Shield } from "lucide-react";

const CONDITIONS = ["New", "Like New", "Good", "Fair", "For Parts"];

export default function CreateListing() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", price: "", category: "", condition: "Good", location: "Florida",
    requires_ffl: false, ffl_notes: "",
  });
  const [imagePaths, setImagePaths] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { isFirearmsPassActive, isAgeVerified } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.marketplace));
  }, []);

  const currentCat = categories.find((c) => c.id === form.category);
  const isFirearm = !!currentCat?.firearm_class;
  const gate = isFirearm && (!isFirearmsPassActive || !isAgeVerified);
  const gateReason = isFirearm
    ? (!isFirearmsPassActive ? "pass" : (!isAgeVerified ? "verify" : null))
    : null;

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImagePaths((prev) => [...prev, data.path]);
      }
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price || !form.category) {
      toast.error("Fill required fields");
      return;
    }
    if (gate) {
      if (gateReason === "pass") return navigate("/subscribe");
      return navigate("/firearms/verify?next=/listings/new");
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form, price: parseFloat(form.price), image_paths: imagePaths,
        requires_ffl: isFirearm ? !!form.requires_ffl : false,
        ffl_notes: isFirearm ? (form.ffl_notes || "") : "",
      };
      const { data } = await api.post("/listings", payload);
      toast.success("Listing posted");
      navigate(`/listings/${data.listing_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post listing");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in" data-testid="create-listing-page">
      <div className="text-xs uppercase tracking-widest text-accent mb-2">List an item</div>
      <h1 className="font-display font-extrabold text-3xl md:text-4xl mb-6">Sell some gear.</h1>

      <form onSubmit={submit} className="space-y-5 bg-card border border-border rounded-md p-6 md:p-8">
        <div>
          <Label>Photos</Label>
          <div className="mt-1.5 grid grid-cols-3 sm:grid-cols-5 gap-3">
            {imagePaths.map((p, i) => (
              <div key={p} className="relative aspect-square rounded-md overflow-hidden bg-muted">
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setImagePaths(imagePaths.filter((_, idx) => idx !== i))}
                  className="absolute top-1 right-1 bg-foreground/80 text-background rounded-full p-1 hover:bg-foreground"
                  data-testid={`btn-remove-image-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <label className="aspect-square rounded-md border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center cursor-pointer text-muted-foreground text-xs gap-1">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span>Add photo</span>
              <input type="file" accept="image/*" multiple onChange={uploadFiles} className="hidden" data-testid="input-upload-images" />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Penn Spinfisher VI 6500" className="mt-1.5" data-testid="input-listing-title" />
          </div>
          <div>
            <Label htmlFor="price">Price (USD) *</Label>
            <Input id="price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="120" className="mt-1.5" data-testid="input-listing-price" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1.5" data-testid="select-listing-category"><SelectValue placeholder="Pick one" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`opt-lcat-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Condition</Label>
            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
              <SelectTrigger className="mt-1.5" data-testid="select-listing-condition"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="location">Location</Label>
            <Input id="location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Tampa, FL" className="mt-1.5" data-testid="input-listing-location" />
          </div>
        </div>

        <div>
          <Label htmlFor="desc">Description</Label>
          <Textarea id="desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Include condition, use history, why you're selling, and pickup options." className="mt-1.5 min-h-[160px]" data-testid="input-listing-description" />
        </div>

        {isFirearm && (
          <div className="border border-secondary/40 bg-secondary/5 rounded-md p-5 space-y-4" data-testid="firearms-section">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
              <div className="text-sm leading-relaxed">
                <div className="font-semibold text-foreground">Firearms transfer options</div>
                <div className="text-muted-foreground mt-1">
                  All firearm sales must comply with federal, Florida, and local law. If your item requires transfer
                  through a Federal Firearms License (FFL) holder, mark it below and describe your preferences.
                </div>
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={!!form.requires_ffl}
                onCheckedChange={(v) => setForm({ ...form, requires_ffl: !!v })}
                className="mt-0.5"
                data-testid="check-requires-ffl"
              />
              <div>
                <div className="text-sm font-medium">Require FFL transfer for this listing</div>
                <div className="text-xs text-muted-foreground">Recommended for handguns, NFA items, and inter-state sales.</div>
              </div>
            </label>
            {form.requires_ffl && (
              <div>
                <Label htmlFor="ffl">FFL notes (dealer preferences, transfer fee, etc.)</Label>
                <Textarea
                  id="ffl" value={form.ffl_notes}
                  onChange={(e) => setForm({ ...form, ffl_notes: e.target.value })}
                  placeholder="e.g. Buyer covers $30 FFL transfer fee. Prefer any Florida FFL near Tampa."
                  className="mt-1.5 min-h-[80px]"
                  data-testid="input-ffl-notes"
                />
              </div>
            )}
            {gate && (
              <div className="text-sm bg-background rounded-md p-3 border border-border">
                {gateReason === "pass" ? (
                  <>You'll need an active <strong>Firearms Pass</strong> to publish this listing. Click below to continue.</>
                ) : (
                  <>You'll need to complete <strong>age & liability verification</strong> to publish this listing.</>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="btn-cancel-listing">Cancel</Button>
          <Button type="submit" disabled={submitting} className={gate ? "bg-secondary hover:bg-secondary/90" : "bg-primary hover:bg-primary/90"} data-testid="btn-submit-listing">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {gate ? (gateReason === "pass" ? "Get Firearms Pass to continue" : "Verify age to continue") : "Post listing"}
          </Button>
        </div>
      </form>
    </div>
  );
}
